import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Link2, MessagesSquare, UserRound } from 'lucide-react';
import { familyApi } from '../api/auth';
import type { Child, Me } from '../api/auth';
import { familyAnnouncementsApi } from '../api/announcements';
import type { FamilyAnnouncement } from '../api/announcements';
import { familyConversationsApi } from '../api/conversations';
import type { FamilyConversationRow } from '../api/conversations';
import { familyEventsApi } from '../api/events';
import type { EventAgendaPage, EventCalendarPage, EventSummary } from '../api/events';
import { AnnouncementAttachments, useKeepAttachmentsSigned } from '../ui/AnnouncementAttachments';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { EventAgenda } from '../ui/EventAgenda';
import { EventSearch } from '../ui/EventSearch';
import { requirementsApi } from '../api/requirements';
import type { FamilyRequirement } from '../api/requirements';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CopyLink } from '../ui/CopyLink';
import { CuratedText } from '../ui/CuratedText';
import { TextField } from '../ui/Field';
import { UnreadDot } from '../ui/UnreadDot';
import { formatInZone, formatTimestamp } from '../lib/datetime';
import { monthLabel, neighbours } from '../lib/eventMonths';
import { EventMonthSection, MonthBar } from '../ui/MonthBar';

/**
 * Every list here is bounded, and it took a browser to notice why.
 *
 * Unbounded, against the seeded dev database, this page rendered **4,165 buttons and 626,811
 * pixels of height**, and took about eight seconds to appear — long enough that the first
 * screenshot of it came back blank. Most of that is smoke-run residue (TROOP-62), but the shape of
 * the bug is real at family scale too: a troop publishes announcements for years, a Scout who
 * stays five years accumulates threads, and "everything, ever" is not what a parent opening this
 * page is looking for.
 *
 * The counts stay in the headings, so the bound is visible rather than a silent truncation — the
 * same argument `ParticipantStrip` makes for its eight chips and a `+N`.
 */
const ANNOUNCEMENTS_SHOWN = 5;
const THREADS_SHOWN = 8;

/**
 * Larger than the others: this one is a to-do list, and truncating somebody's to-do list is a
 * worse trade than truncating a noticeboard. The heading still carries the real total.
 */
const ASKS_SHOWN = 12;

/**
 * A parent's front page — TROOP-70.
 *
 * **It used to be a setup checklist**, and that was the wrong thing to have permanently at the
 * top. It read as though the product did not begin until the child signed in, and for a family
 * whose Scout has no phone — or who would rather their child not be in an app — as though the
 * product were not for them at all. A guardian is a full user of this in their own right; the
 * child's account is optional and depends on the parent having one, never the reverse (rule 11).
 *
 * So: **what is happening, then who my children are, then setup only if any is left.** Events,
 * announcements and threads are peers here, in the order a family actually needs them.
 *
 * **Multi-unit is the ordinary case on this screen and nowhere else.** A member has the unit
 * switcher in {@link AppHeader} and one unit's content at a time; a parent holds no memberships,
 * so the switcher does not mount, and their children may be in two units at once — which is
 * exactly the seeded shape. Every row therefore carries its unit rather than the page being
 * grouped by one: a family scans a single list in time order, and the unit is a qualifier on the
 * row. Grouping would put the same Saturday in two places and make "which is first" a puzzle.
 *
 * It is still the only place a child's account can be set up. A leader cannot, because the rule
 * is that a young person gets in behind an activated guardian — and a leader who could mint the
 * link would be stepping around that rule rather than enforcing it.
 */
export function FamilyScreen({ me, onNavigate }: { me: Me; onNavigate: (path: string) => void }) {
  const [children, setChildren] = useState<Child[] | null>(null);
  const [asks, setAsks] = useState<FamilyRequirement[] | null>(null);
  const [threads, setThreads] = useState<FamilyConversationRow[] | null>(null);
  // The true size of the thread surface, which `threads` no longer knows: that list is one page
  // (TROOP-63). Counting the page would quietly turn the heading's number into the bound itself.
  const [threadTotals, setThreadTotals] = useState<{ total: number; threads: number } | null>(null);
  const [agenda, setAgenda] = useState<EventAgendaPage | null>(null);
  /** The landing view, and the calendar one tap away — TROOP-213, and see `UnitHomeScreen`. */
  const [view, setView] = useState<'agenda' | 'calendar'>('agenda');
  const [calendar, setCalendar] = useState<EventCalendarPage | null>(null);
  const [movingMonth, setMovingMonth] = useState(false);
  const [announcements, setAnnouncements] = useState<FamilyAnnouncement[] | null>(null);
  // The real size of the noticeboard, which `announcements` no longer knows: that list is bounded
  // server-side now, so its length is what was sent rather than what exists (TROOP-96).
  const [announcementTotal, setAnnouncementTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      // Five reads, one screen, in parallel. A parent's children span units, and so does
      // everything asked of them -- which is exactly why all of these live under /family rather
      // than under /units/: a guardian holds no membership, and a unit-scoped route would 404 the
      // person they exist for.
      //
      // The calendar is deliberately not among them (TROOP-213): it is a whole month across every
      // unit the children are in, and nobody has asked for it until they tap View all.
      const [mine, requirements, conversations, events, noticeboard, threadCounts] =
        await Promise.all([
        familyApi.children(),
        requirementsApi.forMyFamily(),
        familyConversationsApi.list(),
        familyEventsApi.agenda(),
        familyAnnouncementsApi.list(),
        familyConversationsApi.unreadTotal(),
      ]);

      setChildren(mine);
      setAsks(requirements);
      setThreads(conversations.rows);
      setThreadTotals(threadCounts);
      setAgenda(events);
      setAnnouncements(noticeboard.rows);
      setAnnouncementTotal(noticeboard.total);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your family.');
    }
  }, []);

  /**
   * Walking to another month is a fresh read, for the reason the unit's calendar gives: the window
   * is the server's to decide, and here it spans every unit the children are in — so neither the
   * slice nor the month counts could be worked out from what is already on screen.
   */
  async function moveTo(month?: string) {
    setMovingMonth(true);
    try {
      setCalendar(await familyEventsApi.calendar(month));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that month.');
    } finally {
      setMovingMonth(false);
    }
  }

  /** View all. Fetched on the first tap and kept after that. */
  async function showCalendar() {
    setView('calendar');
    if (!calendar) await moveTo();
  }

  // Months in the order the window returned them. The rows arrive already ordered by month and
  // then by start time, so nothing here re-sorts -- the server decided both.
  const months: { month: string; label: string; events: EventSummary[] }[] = [];

  for (const event of calendar?.events ?? []) {
    const last = months[months.length - 1];

    if (last && last.month === event.month) {
      last.events.push(event);
    } else {
      months.push({ month: event.month, label: monthLabel(event.month), events: [event] });
    }
  }

  const { previous, next } = neighbours(calendar?.months ?? [], months);

  useEffect(() => {
    void load();
  }, [load]);

  // The noticeboard alone, re-read before its documents' signed URLs lapse (TROOP-255) -- not
  // `load`, which is six reads across every unit the children are in.
  const refreshAnnouncements = useCallback(() => {
    familyAnnouncementsApi
      .list()
      .then((page) => {
        setAnnouncements(page.rows);
        setAnnouncementTotal(page.total);
      })
      .catch(() => {
        // The links on screen stay as they were; the next reload says what is wrong.
      });
  }, []);

  useKeepAttachmentsSigned(announcements, refreshAnnouncements);

  const waiting = children?.filter((c) => !c.hasAccount) ?? [];
  const settled = children?.filter((c) => c.hasAccount) ?? [];
  // From the server's full pass, not from the page on screen. Summing the page would give a
  // badge that falls when somebody else posts, which reads as a number rather than as a bug.
  const unread = threadTotals?.total ?? 0;
  const threadCount = threadTotals?.threads ?? threads?.length ?? 0;
  const outstanding = asks?.filter((a) => !a.completionId) ?? [];

  // A guardian whose children have all left -- aged out, or moved unit -- is a real state rather
  // than an error, and it renders as a sentence rather than as five empty sections.
  const hasStanding =
    (agenda?.upcoming.length ?? 0) +
      (agenda?.justFinished.length ?? 0) +
      (announcements?.length ?? 0) +
      (threads?.length ?? 0) >
    0;

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <h1 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
              Your family
            </h1>
            <p
              className="t-body"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-3) 0 0',
                maxWidth: '52ch',
              }}
            >
              What is coming up, what the unit has said, and every thread your children are in —
              across every unit they belong to. All of it comes with being their parent; there was
              nothing to set up for it.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {/* What is happening -- TROOP-213. The same component the unit's page opens on, not a
                lookalike: rule 13b is that /family mirrors /units in routes and never in rules, and
                "what does this page show" is a rule. Only the row differs, because a parent's spans
                units and carries which one each event is in. */}
            {view === 'agenda' && agenda && (agenda.upcoming.length > 0 || agenda.justFinished.length > 0) && (
              <FamilyGroup
                title="Events"
                note="Every unit your children are in, on one list. Times are the unit's own rather than yours."
              >
                <EventAgenda
                  agenda={agenda}
                  onViewAll={() => void showCalendar()}
                  renderItem={(event) => (
                    <EventRow
                      key={event.id}
                      event={event}
                      onOpen={() => onNavigate(`/family/events/${event.id}`)}
                    />
                  )}
                />
              </FamilyGroup>
            )}

            {view === 'calendar' && (
              <FamilyGroup
                // The count was the truncation showing in the heading: it always read 25, because
                // that was the cap, and nothing said so (TROOP-13). The window says which months
                // it is showing instead, and the bar below reaches the rest.
                title="Calendar"
                note="Every unit your children are in, on one calendar. Times are the unit's own rather than yours."
              >
                {/* Reachable back again, which is half of what makes the agenda a landing view
                    rather than a redirect. */}
                <div>
                  <Button variant="secondary" onClick={() => setView('agenda')}>
                    Back to what is coming up
                  </Button>
                </div>

                {/* Search, on the all-events view and nowhere else -- TROOP-214. The unit door's
                    sibling and the same component, for the rule-13b reason every other control on
                    this page is: a parent who is also a leader should not learn this twice. What
                    differs is the fetch, which spans every unit their children are in. */}
                {(calendar?.months.length ?? 0) > 0 && (
                  <EventSearch
                    search={(query) => familyEventsApi.search(query)}
                    renderItem={(event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onOpen={() => onNavigate(`/family/events/${event.id}`)}
                      />
                    )}
                  />
                )}

                {/* The same control the unit's calendar uses -- the same component, not a
                    lookalike. A parent who is also a leader should not learn this twice. */}
                <MonthBar
                  index={calendar?.months ?? []}
                  shown={months}
                  previous={previous}
                  next={next}
                  busy={movingMonth}
                  onAnchorChange={(month) => void moveTo(month)}
                />

                {months.map((group) => (
                  <EventMonthSection
                    key={group.month}
                    label={group.label}
                    items={group.events}
                    renderItem={(event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onOpen={() => onNavigate(`/family/events/${event.id}`)}
                      />
                    )}
                  />
                ))}
              </FamilyGroup>
            )}

            {(announcements?.length ?? 0) > 0 && (
              <FamilyGroup
                title={`From the unit \u00b7 ${announcementTotal}`}
                note="Pinned first, then newest -- the same order everybody else reads them in."
              >
                {announcements!.slice(0, ANNOUNCEMENTS_SHOWN).map((a) => (
                  <AnnouncementRow key={a.id} announcement={a} />
                ))}
              </FamilyGroup>
            )}

            {(threads?.length ?? 0) > 0 && (
              <FamilyGroup
                title={unread > 0 ? `Conversations \u00b7 ${unread} new` : `Conversations \u00b7 ${threadCount}`}
                note="Every thread your children are in, across every unit. Reading them comes with being their parent; everyone in a thread is told that parents can read it."
              >
                {threads!.slice(0, THREADS_SHOWN).map((thread) => (
                  <ThreadCard
                    key={thread.id}
                    thread={thread}
                    onOpen={() => onNavigate(`/family/conversations/${thread.id}`)}
                  />
                ))}

                {/* The way through to the grouped page. This section stays a summary of the most
                    recent few rather than becoming the list -- the heading already carries the true
                    count, and a parent opening this page on a Thursday wants what moved, not an
                    index. What was missing was any route to the index at all. */}
                <button
                  type="button"
                  onClick={() => onNavigate('/family/conversations')}
                  className="t-caption text-left"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-secondary)',
                    padding: 'var(--space-2) var(--space-1)',
                    minHeight: 'var(--tap-min)',
                    textDecoration: 'underline',
                  }}
                >
                  See all {threadCount} conversations, by channel and event
                </button>

                {/* Where a report goes once it has been raised (TROOP-12, §5). It sits under
                    Conversations because that is what it is about, and it is offered to every
                    parent rather than to reviewers only: the whole argument for flagging is that
                    the person who raised one can see whether anybody looked. A parent with nothing
                    reported finds an empty page saying so, which is a fair price for the door
                    being where they would look for it. */}
                <button
                  type="button"
                  onClick={() => onNavigate('/family/reports')}
                  className="t-caption text-left"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-secondary)',
                    padding: 'var(--space-2) var(--space-1)',
                    minHeight: 'var(--tap-min)',
                    textDecoration: 'underline',
                  }}
                >
                  Messages you or your children reported
                </button>
              </FamilyGroup>
            )}

            {threads?.length === 0 && children && children.length > 0 && (
              <FamilyGroup title="Conversations">
                <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                  Nothing yet. When a unit starts a thread your children are in, it appears here on
                  its own.
                </p>
              </FamilyGroup>
            )}

            {/* Outstanding only, and the heading counted only those all along -- so rendering the
                answered ones underneath was the list disagreeing with its own title. A parent
                comes here to find what is left, not to re-read what they have already done. */}
            {outstanding.length > 0 && (
              <FamilyGroup
                title={`Waiting on you · ${outstanding.length}`}
                note="What your children's units have asked for. Ticking something here says you have done it."
              >
                {outstanding.slice(0, ASKS_SHOWN).map((ask) => (
                  <AskCard
                    key={`${ask.requirementId}-${ask.childPersonId}`}
                    ask={ask}
                    onChanged={load}
                  />
                ))}
              </FamilyGroup>
            )}

            {children?.length === 0 && (
              <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
                Nobody is recorded as your child yet. A leader in your unit adds that when
                they put them on the roster — you do not need to do anything, and they will
                appear here on their own.
              </p>
            )}

            {/* Children, but no unit. Their membership ended -- they aged out, or the family
                moved -- and the honest thing is to say so rather than render five empty
                headings and let a parent wonder whether the page is broken. */}
            {children && children.length > 0 && calendar && !hasStanding && (
              <p className="t-body" style={{ color: 'var(--color-text-secondary)', maxWidth: '52ch' }}>
                Nothing to show. None of your children are on a unit's roster at the moment, so
                there is no calendar or noticeboard for you to read. If that looks wrong, a leader
                in the unit can put them back on it.
              </p>
            )}

            {/* Setup, and only while there is any left.

                Phrased as an option rather than an obligation, because it is one: everything above
                this point already works, and a Scout with no phone is an ordinary family rather
                than an unfinished one. The heading used to read "Not signed up yet", which framed
                a deliberate choice as an outstanding task.

                Split rather than one flat list, so a parent with four children can see what is
                left without reading every card, and a child added months later lands at the top
                rather than in the middle of the ones already done. */}
            {waiting.length > 0 && (
              <FamilyGroup
                title={waiting.length === 1 ? 'Their own sign-in' : `Their own sign-in · ${waiting.length}`}
                note="Optional, and nothing above depends on it. Give them a link whenever you are ready — or never, and use this page yourself."
              >
                {waiting.map((child) => (
                  <ChildCard key={child.personId} child={child} onChanged={load} />
                ))}
              </FamilyGroup>
            )}

            {settled.length > 0 && (
              <FamilyGroup title={`Signed in already · ${settled.length}`}>
                {settled.map((child) => (
                  <ChildCard key={child.personId} child={child} onChanged={load} />
                ))}
              </FamilyGroup>
            )}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

/**
 * One event on a parent's calendar.
 *
 * **The unit is on the row, not a heading.** A family with children in two units scans one list in
 * time order; grouping by unit would put the same Saturday in two places. Rule 16 does not bite
 * here — no subgroup word appears — but if one ever does it must come from the unit's own data,
 * and this screen can be showing two units with two different words at once.
 *
 * The time renders in the **event's** zone. This machine and the seeded troop are both
 * America/New_York, so a render that wrongly used the viewer's zone still looks right there; only
 * an event carrying an override tells the two apart.
 */
function EventRow({ event, onOpen }: { event: EventSummary; onOpen: () => void }) {
  const cancelled = event.status === 'Cancelled';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center w-full text-left"
      style={{
        background: 'var(--color-surface-raised)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
        gap: 'var(--space-3)',
        minHeight: 'var(--tap-min)',
      }}
    >
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center shrink-0"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <CalendarDays size={18} />
      </span>

      <span className="flex flex-col flex-1 min-w-0">
        <span
          className="t-body-strong"
          style={{
            color: 'var(--color-text-primary)',
            textDecoration: cancelled ? 'line-through' : undefined,
          }}
        >
          {event.title}
        </span>
        <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
          {formatInZone(event.startsAt, event.timeZoneId)} &middot; {event.unitName}
          {event.locationName ? ` · ${event.locationName}` : ''}
        </span>

        {/* What this one is about, when somebody has said (TROOP-158). Absent, never a
            placeholder -- most events on a calendar have not been planned yet, and a row
            with nothing under it is the right rendering of that. */}
        {event.theme && (
          <span
            className="t-caption"
            style={{ color: 'var(--color-text-secondary)', display: 'block' }}
          >
            {event.theme}
          </span>
        )}

        {/* A family who already saw it on the calendar needs telling it is off, and why. That is
            the whole point of an event being a record rather than a message that scrolled away. */}
        {cancelled && (
          <span className="t-caption" style={{ color: 'var(--color-status-blocked)' }}>
            Cancelled{event.cancellationReason ? ` · ${event.cancellationReason}` : ''}
          </span>
        )}
      </span>

      {event.unreadCount > 0 && <UnreadDot count={event.unreadCount} />}
    </button>
  );
}

/**
 * One announcement, read in place rather than opened. It is standing information — a headline and
 * a short body — so hiding the body behind a tap would make a parent work for two sentences.
 */
function AnnouncementRow({ announcement }: { announcement: FamilyAnnouncement }) {
  return (
    <article
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {announcement.title}
        </h3>
        {announcement.unreadCount > 0 && <UnreadDot count={announcement.unreadCount} />}
      </div>

      {announcement.isPinned && (
        <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 'var(--space-1) 0 0' }}>
          Pinned
        </p>
      )}

      {/* The same renderer as the unit's card, so a parent's door draws the same links with the
          same host beside them (TROOP-255). */}
      <CuratedText
        text={announcement.body}
        style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-2)' }}
      />

      {/* The same handouts a member sees on the unit's page, through the same component -- a
          parent's door that dropped them would be the one place a family could not get the
          packet (TROOP-255). */}
      <AnnouncementAttachments attachments={announcement.attachments} />

      {/* Name AND role -- a name alone does not tell a new family whether this is the unit
          speaking or somebody's opinion. And the unit, because this list spans two of them. */}
      <p
        className="t-caption"
        style={{ color: 'var(--color-text-muted)', margin: 'var(--space-2) 0 0' }}
      >
        {announcement.authorDisplayName}
        {announcement.authorRole ? `, ${announcement.authorRole}` : ''} &middot;{' '}
        {announcement.unitName} &middot; {formatTimestamp(announcement.publishedAt)}
      </p>
    </article>
  );
}

function FamilyGroup({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 'var(--space-6)' }}>
      <h2
        className="t-label"
        style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
      >
        {title}
      </h2>
      {note && (
        <p
          className="t-caption"
          style={{
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--space-3)',
            maxWidth: '46ch',
          }}
        >
          {note}
        </p>
      )}
      <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
        {children}
      </div>
    </section>
  );
}

/**
 * One thread on a parent's list, and why they can see it.
 *
 * The reason is rendered rather than left implicit. `Guardian` is I4 -- they are reading a thread
 * they are not in, which should never look like eavesdropping; anything else means they are in it
 * themselves, which is a different thing and worth saying.
 *
 * **Both facts render, because since TROOP-58 they stopped being alternatives.** Almost every row
 * used to be `Guardian` and said whose parent you are; almost every row is now `Participant`, and
 * a version that only said *"You are in this thread"* would drop the one cue telling a parent
 * which of their children a thread concerns -- at exactly the point where that list got longer,
 * because it spans every unit their children are in.
 */
function ThreadCard({ thread, onOpen }: { thread: FamilyConversationRow; onOpen: () => void }) {
  const kids = thread.childNames;

  // Named up to two, counted beyond that. A whole-unit thread contains every child a family has,
  // and a parent of four does not want the row to be a list of their own children -- they want to
  // know which thread it is. Two is where naming stops being the more useful of the two.
  const whose =
    kids.length === 0
      ? null
      : kids.length <= 2
        ? `${kids.join(' and ')} ${kids.length === 1 ? 'is' : 'are'} in it`
        : `${kids.length} of your children are in it`;

  // Standing first, then whose, because since TROOP-58 they are two facts rather than a choice
  // between two. "You are in this thread" alone is true and useless on a list spanning two units;
  // naming the children alone reads as though a parent were only watching.
  const standing = thread.basis === 'Guardian' ? 'You can read this' : 'You are in this thread';

  const reason = whose ? `${standing} · ${whose}` : standing;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center w-full text-left"
      style={{
        background: 'var(--color-surface-raised)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
        gap: 'var(--space-3)',
        minHeight: 'var(--tap-min)',
      }}
    >
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center shrink-0"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <MessagesSquare size={18} />
      </span>

      <span className="flex flex-col flex-1 min-w-0">
        <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
          {thread.title}
        </span>
        <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
          {thread.unitName} &middot; {reason}
        </span>
      </span>

      {thread.unreadCount > 0 && <UnreadDot count={thread.unreadCount} />}
    </button>
  );
}

function ChildCard({ child, onChanged }: { child: Child; onChanged: () => Promise<void> }) {
  const [email, setEmail] = useState(child.email ?? '');
  // `copySentTo` comes from the server (TROOP-223): a parent's own copy is only sent when they
  // have an address on file, and this card cannot see which branch ran.
  const [link, setLink] = useState<
    { url: string; expiresAt: string; copySentTo: string | null } | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  // Whether the last save withdrew a link already sent (TROOP-232). The server's answer, so the
  // card reports what happened rather than what it assumes happened -- and it may be false on a
  // save that did change something, when the only outstanding link was bound to the new address.
  const [cancelledLink, setCancelledLink] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = (child.email ?? '') !== email.trim();

  async function saveEmail() {
    setBusy(true);
    setError(null);
    try {
      const result = await familyApi.setEmail(child.personId, email.trim() || null);
      setSaved(true);

      // TROOP-232: the server withdraws a link bound to the address this replaced, in the same
      // transaction, so a copy of it left on screen is a URL the parent could still pass on and
      // which now refuses whoever opens it.
      //
      // **On the server's answer, never on `dirty`.** `dirty` is a raw string comparison and the
      // revocation is `PersonEmail.Normalize`'s -- trim and lower -- so a parent whose keyboard
      // capitalised the address saves a "change" that withdraws nothing, and dropping the panel
      // there would hide a live link directly beside the hint saying it had been cancelled. The
      // `rule-auditor` caught exactly that, on the screen whose sibling got it right.
      if (result.cancelledSignUpLink) setLink(null);
      setCancelledLink(result.cancelledSignUpLink);

      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that address.');
    } finally {
      setBusy(false);
    }
  }

  async function issueLink() {
    setBusy(true);
    setError(null);
    try {
      // Saved first, so the address the parent just typed is not silently lost
      // when the page reloads behind the link.
      //
      // And the save's own answer is acted on rather than dropped: if it withdrew the link on
      // screen, that copy is dead the moment this line returns, and the issue below can still fail
      // (a 500, a dropped connection) and leave the parent looking at a URL beside "Could not
      // create a sign-up link". Before TROOP-232 that stale URL still worked, so dropping the
      // answer cost nothing.
      if (dirty) {
        const saveResult = await familyApi.setEmail(child.personId, email.trim() || null);
        if (saveResult.cancelledSignUpLink) setLink(null);
      }

      const issued = await familyApi.claimLink(child.personId);
      setLink({ url: issued.url, expiresAt: issued.expiresAt, copySentTo: issued.copySentTo });

      // The save's caption is about the link that was just replaced, and the panel below is about
      // the one that replaced it -- so it must not outlive this. Left standing it renders "the
      // sign-up link already sent no longer works" directly above a copy of the link that does,
      // and a parent who believes it sends another, which revokes this one, indefinitely.
      setSaved(false);
      setCancelledLink(false);

      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create a sign-up link.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-5)',
      }}
    >
      <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
        {/* Same shape language as the roster: a rounded square for a youth. */}
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center shrink-0"
          style={{
            width: 26,
            height: 26,
            borderRadius: 'var(--radius-sm)',
            border: '1.5px dashed var(--color-border-strong)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <UserRound size={13} />
        </span>
        <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
          {child.displayName}
        </span>
      </div>

      {child.unitNames.length > 0 && (
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
        >
          {child.unitNames.join(', ')}
        </p>
      )}

      {error && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: 'var(--space-3) 0 0' }}
        >
          {error}
        </p>
      )}

      {child.hasAccount ? (
        <p
          className="t-body"
          style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-4) 0 0' }}
        >
          <strong style={{ color: 'var(--color-status-compliant)' }}>Signed up.</strong>{' '}
          {child.displayName.split(' ')[0]} can sign in on their own. You still see everything
          they are part of.
        </p>
      ) : (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <TextField
            label={`${child.displayName.split(' ')[0]}'s email`}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSaved(false);
              setCancelledLink(false);
            }}
            // Still nobody else's to read — rule 10, and that half has not moved. What has moved
            // is the second sentence: it said "the sign-up link works either way", which was true
            // while the link bound whichever account redeemed it and is exactly the premise
            // TROOP-224 removed. The link is now sent to this address and only this address can
            // redeem it, so the address is what a login is made of rather than a detail beside one.
            // The third sentence is TROOP-232's, and it is a standing fact about the control rather
            // than a report on a branch: changing or clearing the address withdraws the link
            // already sent to the old one. It used to cost only the *next* link, so a parent who
            // cleared an address to call off a sign-up had called off nothing.
            hint="Only you can see this — not leaders, not administrators. A sign-up link goes here, and only this address can use it. Changing or clearing it cancels a link you have already sent."
          />

          {dirty && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button
                variant="secondary"
                onClick={() => void saveEmail()}
                disabledReason={busy ? 'Saving…' : undefined}
              >
                Save address
              </Button>
            </div>
          )}

          {saved && !dirty && (
            <p
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0' }}
            >
              {/* The second sentence only when the server says it happened (TROOP-232). The hint
                  above states the rule for every save; this states the event, and a card that
                  announced it unconditionally would tell a parent they had cancelled a link when
                  there had never been one -- the same mistake `copySentTo` exists to avoid. */}
              Saved.
              {cancelledLink
                ? ` The sign-up link already sent to ${child.displayName.split(' ')[0]} no longer works.`
                : ''}
            </p>
          )}

          <div style={{ marginTop: 'var(--space-4)' }}>
            <Button
              variant="primary"
              onClick={() => void issueLink()}
              // The address box is directly above this button, so the server's 409 is a round
              // trip to learn something the screen already knows (TROOP-224). The route still
              // refuses -- this is what renders, not what enforces -- and it says why, which is
              // the whole point of `disabledReason` over `disabled`.
              disabledReason={
                busy
                  ? 'Working on it…'
                  : email.trim()
                    ? undefined
                    : `Add ${child.displayName.split(' ')[0]}'s email address first — the link is sent there, and only that address can use it.`
              }
            >
              <Link2 size={16} aria-hidden="true" />
              {/* "Send a new link", never "Resend" (TROOP-223). Issuing revokes every outstanding
                  link, so a second one necessarily kills the mail already in the child's inbox --
                  a Resend label promises a duplicate while delivering a replacement, which is a
                  control drawn as the state it records. */}
              {link ? 'Send a new link' : 'Email a sign-up link'}
            </Button>
            <p
              className="t-caption"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-2) 0 0',
                maxWidth: '48ch',
              }}
            >
              Only if you want {child.displayName.split(' ')[0]} to have their own login. Plenty
              of families never do — you see everything either way.
            </p>
          </div>
        </div>
      )}

      {link && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <CopyLink url={link.url} label={`A copy of what ${child.displayName.split(' ')[0]} was sent`} />
          <p
            className="t-caption"
            style={{
              color: 'var(--color-text-secondary)',
              margin: 'var(--space-2) 0 0',
              maxWidth: '48ch',
            }}
          >
            {/* Rewritten for TROOP-224, and the old sentence is worth knowing about: it said
                "whoever opens it is who the account belongs to afterwards, so don't open it
                yourself", which was an accurate warning about a bearer credential. The link is
                now bound to the address above, so opening it yourself no longer binds your
                account to your child's record -- it simply will not work. Warning about a hazard
                that has been removed teaches the wrong thing about how the link behaves.

                The first sentence is TROOP-223's: it is emailed now, and the parent's own copy
                arrives separately rather than as a second name on the child's message. The copy
                is announced only when the server says it sent one -- it needs an address on file
                for you, and a card that said so unconditionally would send a parent hunting for a
                message nobody composed. */}
            Emailed to {email.trim() || 'the address above'}
            {link.copySentTo ? `, and a copy to you at ${link.copySentTo}` : ''}. It only works
            from that address &mdash; a Google account on it, or a code we email there &mdash; so
            opening it yourself will not sign you in as{' '}
            {child.displayName.split(' ')[0]}. It works once.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * One thing a unit has asked of one child, and the parent's way of saying it is done.
 *
 * A link shows the host it actually goes to. That is §7's phishing mitigation and it is not
 * optional decoration: this is precisely the screen where a parent has been emailed a link and
 * asked to enter personal details on somebody else's site.
 */
function AskCard({ ask, onChanged }: { ask: FamilyRequirement; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <span className="t-body-strong" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
        {ask.title}
      </span>
      <span className="t-caption" style={{ color: 'var(--color-text-muted)', display: 'block' }}>
        {ask.childName} &middot; {ask.eventTitle} &middot; {ask.unitName}
      </span>

      {ask.body && (
        <p className="t-body" style={{ color: 'var(--color-text-primary)', marginTop: 'var(--space-2)' }}>
          {ask.body}
        </p>
      )}

      {ask.externalUrl && (
        <p style={{ marginTop: 'var(--space-2)' }}>
          <a href={ask.externalUrl} target="_blank" rel="noopener noreferrer" className="t-body">
            Open it
          </a>{' '}
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            &mdash; goes to <strong>{ask.externalHost}</strong>
          </span>
        </p>
      )}

      {failed && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {failed}
        </p>
      )}

      <p style={{ marginTop: 'var(--space-3)', marginBottom: 0 }}>
        {ask.completionId ? (
          <span className="t-caption" style={{ color: 'var(--color-status-compliant)' }}>
            {/* The tier in words, never a bare tick: a returned form and a parent saying they did
                it are different kinds of proof, and this screen is where the difference starts. */}
            {ask.evidenceTier === 'Artifact' ? 'Form received' : 'You have confirmed this'}
          </span>
        ) : ask.kind === 'Document' ? (
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            Hand the filled-in form to a leader.
          </span>
        ) : (
          <Button
            variant="secondary"
            disabledReason={busy ? 'One moment.' : undefined}
            onClick={async () => {
              setBusy(true);
              setFailed(null);

              try {
                await requirementsApi.completeForChild(ask.requirementId, ask.childPersonId);
                await onChanged();
              } catch (e) {
                setFailed(e instanceof Error ? e.message : 'That did not work.');
              } finally {
                setBusy(false);
              }
            }}
          >
            {ask.kind === 'ExternalLink' ? 'I have done this' : 'I have read this'}
          </Button>
        )}
      </p>
    </div>
  );
}
